import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const location = useLocation();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigationItems = [
    { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
    { label: 'New Quote', path: '/new-quote', icon: 'Plus' },
    { label: 'Feedback', path: '/feedback-dashboard', icon: 'MessageSquare' },
    { label: 'Team', path: '/team-management', icon: 'Users' },
    { label: 'Vault', path: '/digital-vault', icon: 'Shield' },
  ];

  const isActive = (path) => location?.pathname === path;

  const handleLogout = () => {
    // Logout logic here
    console.log('Logging out...');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-card border-b border-border elevation-1">
      <div className="flex items-center justify-between h-20 px-6">
        {/* Company Logo */}
        <Link to="/dashboard" className="flex items-center">
          <div className="h-16 flex items-center">
            <img 
              src="/assets/images/JEWELTHREAD_Vector-1757685590897.png" 
              alt="JewelThread Logo"
              className="h-full w-auto object-contain"
              style={{ backgroundColor: 'transparent' }}
            />
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-1">
          {navigationItems?.map((item) => (
            <Link
              key={item?.path}
              to={item?.path}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-luxury text-sm font-medium ${
                isActive(item?.path)
                  ? 'bg-accent/10 text-accent' :'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              {item?.icon === 'Plus' ? (
                <div className="w-5 h-5 bg-accent text-white border border-accent rounded-lg flex items-center justify-center">
                  <Icon name={item?.icon} size={12} />
                </div>
              ) : (
                <Icon name={item?.icon} size={16} />
              )}
              <span>{item?.label}</span>
            </Link>
          ))}
        </nav>

        {/* Mobile Menu Button */}
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={20} />
        </Button>

        {/* User Profile */}
        <div className="hidden lg:flex items-center space-x-4">
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className="flex items-center space-x-2"
            >
              <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
                <Icon name="User" size={16} color="var(--color-secondary-foreground)" />
              </div>
              <span className="text-sm font-medium">Profile</span>
              <Icon name="ChevronDown" size={16} />
            </Button>

            {isProfileOpen && (
              <div className="absolute top-full right-0 mt-2 w-48 bg-popover border border-border rounded-lg elevation-2 py-2">
                <div className="px-4 py-2 border-b border-border">
                  <p className="text-sm font-medium text-popover-foreground">John Doe</p>
                  <p className="text-xs text-muted-foreground">john@jewelthread.com</p>
                </div>
                <Link
                  to="/profile-edit"
                  className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-luxury"
                  onClick={() => setIsProfileOpen(false)}
                >
                  <Icon name="Settings" size={16} />
                  <span>Edit Profile</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-luxury"
                >
                  <Icon name="LogOut" size={16} />
                  <span>Sign out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Mobile Navigation Overlay */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-20 bg-background/95 backdrop-blur-sm z-40">
          <div className="p-6">
            <nav className="space-y-2">
              {navigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-luxury ${
                    isActive(item?.path)
                      ? 'bg-accent/10 text-accent' :'text-foreground hover:bg-muted'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item?.icon === 'Plus' ? (
                    <div className="w-6 h-6 bg-accent text-white border border-accent rounded-lg flex items-center justify-center">
                      <Icon name={item?.icon} size={14} />
                    </div>
                  ) : (
                    <Icon name={item?.icon} size={20} />
                  )}
                  <span className="font-medium">{item?.label}</span>
                </Link>
              ))}
            </nav>

            <div className="mt-8 pt-6 border-t border-border">
              <div className="flex items-center space-x-3 px-4 py-3">
                <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center">
                  <Icon name="User" size={20} color="var(--color-secondary-foreground)" />
                </div>
                <div>
                  <p className="font-medium text-foreground">John Doe</p>
                  <p className="text-sm text-muted-foreground">john@jewelthread.com</p>
                </div>
              </div>
              <Link
                to="/profile-edit"
                className="flex items-center space-x-3 w-full px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-luxury mt-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Icon name="Settings" size={20} />
                <span>Edit Profile</span>
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-3 w-full px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-luxury mt-2"
              >
                <Icon name="LogOut" size={20} />
                <span>Sign out</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;